import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { AddUsersToModulesAsParticipantsSuccess, RefreshModuleUsersOnlineState, RemoveUsersFromModulesAsParticipantsSuccess } from 'app/library/module-user/actions';
import { AddModuleUsers } from 'app/library/module-user/actions/add-entities.action';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleUserState } from 'app/library/module-user/models/module-user.state';
import { ModuleState } from 'app/library/module/models/module.state';
import { UserService } from 'app/library/user/services/user.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';
import { User } from 'app/projects/user/src/lib/models/user';
import { map } from 'rxjs/operators';
import { ModuleUserExtended } from '../../module-user/models/module-user.extended.model';
import { MyModulesStateModel } from '../../my-profile-modules/models/my-modules.state-model';
import {
    DeselectSelectedFilteredParticipants,
    FetchCourseOfferingAssignedCertificates,
    FetchCourseOfferingAssignedCertificatesFailure,
    FetchCourseOfferingAssignedCertificatesSuccess,
    FetchCourseOfferingParticipant,
    FetchCourseOfferingParticipantFailure,
    FetchCourseOfferingParticipantSuccess,
    FetchCourseOfferingParticipants,
    FetchCourseOfferingParticipantsFailure,
    FetchCourseOfferingParticipantsSuccess,
    FetchProgramExecutionCourseOfferingParticipants,
    FetchProgramExecutionCourseOfferingParticipantsFailure,
    FetchProgramExecutionCourseOfferingParticipantsSuccess,
    FetchProgramParticipantBookings,
    FetchProgramParticipantBookingsFailure,
    FetchProgramParticipantBookingsSuccess,
    NotifyAllProgramExecutionCourseOfferingParticipants,
    NotifyAllProgramExecutionCourseOfferingParticipantsFailure,
    NoNotifyAllProgramExecutionCourseOfferingParticipantsSuccess,
    NotifySelectedProgramExecutionCourseOfferingParticipants,
    NotifySelectedProgramExecutionCourseOfferingParticipantsFailure,
    NotifySelectedProgramExecutionCourseOfferingParticipantsSuccess,
    SearchCourseOfferingParticipants,
    SelectFilteredParticipants,
    SetCourseOfferingParticipants,
    SortCourseOfferingParticipants,
    ToggleSelectedParticipants,
    UnbookParticipant,
    UnbookParticipantFailure,
    UnbookParticipantSuccess,
    UnsetCourseOfferingParticipants,
    NotifyAllCourseOfferingParticipants,
    NoNotifyAllCourseOfferingParticipantsSuccess,
    NotifyAllCourseOfferingParticipantsFailure,
    NotifySelectedCourseOfferingParticipants,
    NotifySelectedCourseOfferingParticipantsSuccess,
    NotifySelectedCourseOfferingParticipantsFailure,
    FetchProgramManagerBookings,
    FetchProgramManagerBookingsSuccess,
    FetchProgramManagerBookingsFailure,
    UnbookManager,
    UnbookManagerSuccess,
    UnbookManagerFailure,
} from '../actions';
import { CourseOfferingParticipantsApiService } from '../services/course-offering-participants.api-service';
import { CourseOfferingParticipantsStateModel } from './course-offering-participants.state-model';
import { SimpleCertificate } from 'app/library/certificate/models/simple-certificate.model';
import { HttpErrorResponse } from '@angular/common/http';

@State<CourseOfferingParticipantsStateModel>({
    name: 'courseOfferingParticipants',
    defaults: new CourseOfferingParticipantsStateModel(),
})
@Injectable()
export class CourseOfferingParticipantsState extends SubentityState<ModuleUser> {
    @Selector([ModuleUserState.getEntitiesAsMap])
    static getFilteredEntities(state: MyModulesStateModel, moduleUsers: Map<string, ModuleUser>): ModuleUser[] {
        return UtilsState.getFilteredEntities(state, moduleUsers);
    }

    @Selector([ModuleUserState.getEntitiesAsMap])
    static getEntities(state: MyModulesStateModel, moduleUsers: Map<string, ModuleUser>): ModuleUser[] {
        return UtilsState.getEntities(state, moduleUsers);
    }

    @Selector()
    static getSelectedEntityIds(state: CourseOfferingParticipantsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getFiltersList(state: CourseOfferingParticipantsStateModel): string[] {
        return state.filtered;
    }

    @Selector()
    static getSearchTerm(state: CourseOfferingParticipantsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: CourseOfferingParticipantsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: CourseOfferingParticipantsStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: CourseOfferingParticipantsStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getPageSize(state: CourseOfferingParticipantsStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: CourseOfferingParticipantsStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: CourseOfferingParticipantsStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    @Selector()
    static isFetchingList(state: CourseOfferingParticipantsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getAssignedCertificates(state: CourseOfferingParticipantsStateModel): SimpleCertificate[] {
        return state.assignedCertificates;
    }

    @Selector()
    static getNumberOfParticipants(state: CourseOfferingParticipantsStateModel): number {
        return state.numberOfParticipants;
    }

    constructor(
        private _courseOfferingParticipantsService: CourseOfferingParticipantsApiService,
        private _store: Store,
        private _userService: UserService,
        private _featureService: FeatureService
    ) {
        super(null, 0);

        this._propstoSearch = ['user', ...User.props_to_search];
    }

    @Action(SetCourseOfferingParticipants)
    setAll(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: SetCourseOfferingParticipants): void {
        this._setSubentities(ctx, action, AddModuleUsers);

        setTimeout(() => {
            const moduleManagersWithUser = this._store.selectSnapshot(CourseOfferingParticipantsState.getEntities) as ModuleUserExtended[];

            if (moduleManagersWithUser && this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                this._store.dispatch(new RefreshModuleUsersOnlineState(moduleManagersWithUser));
                this._userService.setOnlineStateInterval(RefreshModuleUsersOnlineState, CourseOfferingParticipantsState.getEntities);
            }
        });
    }

    @Action(UnsetCourseOfferingParticipants)
    unsetAll(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        this._unsetSubentities(ctx);

        ctx.patchState({
            totalCount: 0,
        });
    }

    @Action(FetchCourseOfferingParticipants)
    fetchCourseOfferingParticipants(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: FetchCourseOfferingParticipants): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCourseOfferingParticipants,
            FetchCourseOfferingParticipantsSuccess,
            FetchCourseOfferingParticipantsFailure,
            this._courseOfferingParticipantsService
                .getCourseOfferingParticipants(
                    action.payload.courseOfferingId,
                    action.payload.search,
                    action.payload.sortBy,
                    action.payload.sortOrder,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.withoutCertificate,
                    action.payload.selectedCertificateIds,
                    action.payload.selectedStatusIds,
                    action.payload.selectedExecutionIds,
                    action.payload.dateRangeFilters,
                    action.payload.audienceIds,
                    action.payload.teamIds,
                    action.payload.extendedAttributesFilters,
                    action.payload.isUnassignedSelectedAudiences,
                    action.payload.isUnassignedSelectedTeams,
                    action.payload.excludedTeamIds,
                    action.payload.teamRoleIds,
                    action.payload.columns
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                            numberOfParticipants: data.number_of_participants,
                        });
                        return data.data.map((query) => new ModuleUserExtended(query));
                    })
                )
        );
    }

    @Action(FetchProgramExecutionCourseOfferingParticipants)
    fetchProgramExecutionCourseOfferingParticipants(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: FetchProgramExecutionCourseOfferingParticipants): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCourseOfferingParticipants,
            FetchProgramExecutionCourseOfferingParticipantsSuccess,
            FetchProgramExecutionCourseOfferingParticipantsFailure,
            this._courseOfferingParticipantsService
                .getProgramExecutionCourseOfferingParticipants(
                    action.payload.courseOfferingId,
                    action.payload.programExecutionId,
                    action.payload.search,
                    action.payload.sortBy,
                    action.payload.sortOrder,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.withoutCertificate,
                    action.payload.selectedCertificateIds,
                    action.payload.selectedStatusIds,
                    action.payload.selectedExecutionIds,
                    action.payload.dateRangeFilters,
                    action.payload.audienceIds,
                    action.payload.teamIds,
                    action.payload.extendedAttributesFilters,
                    action.payload.isUnassignedSelectedAudiences,
                    action.payload.isUnassignedSelectedTeams,
                    action.payload.excludedTeamIds,
                    action.payload.teamRoleIds,
                    action.payload.columns
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                            numberOfParticipants: data.number_of_participants,
                            fetchingList: false,
                        });
                        return data.data.map((query) => new ModuleUserExtended(query));
                    })
                )
        );
    }

    @Action(FetchCourseOfferingParticipantsSuccess)
    fetchAllSuccess(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchCourseOfferingParticipantsFailure)
    fetchAllFailure(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchCourseOfferingParticipant)
    fetchModuleParticipant(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: FetchCourseOfferingParticipant): void {
        this._courseOfferingParticipantsService.getModuleParticipant(action.payload.moduleId, action.payload.userId).subscribe(
            (entity) => {
                this._store.dispatch(new FetchCourseOfferingParticipantSuccess(entity));
            },
            () => {
                this._store.dispatch(new FetchCourseOfferingParticipantFailure());
            }
        );
    }

    @Action(FetchCourseOfferingAssignedCertificates)
    fetchCourseOfferingAssignedCertificates(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: FetchCourseOfferingAssignedCertificates): void {
        this._courseOfferingParticipantsService.getCourseOfferingAssignedCertificates(action.payload.programExecutionId, action.payload.courseOfferingId).subscribe(
            (entities) => {
                ctx.patchState({
                    assignedCertificates: entities,
                });
                this._store.dispatch(new FetchCourseOfferingAssignedCertificatesSuccess(entities));
            },
            () => {
                this._store.dispatch(new FetchCourseOfferingAssignedCertificatesFailure());
            }
        );
    }

    @Action(UnbookParticipant)
    deleteParticipantEntity(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: UnbookParticipant): void {
        this._courseOfferingParticipantsService.unbookParticipant(action.payload).subscribe(
            () => {
                ctx.dispatch(new UnbookParticipantSuccess());
            },
            (error) => {
                ctx.dispatch(new UnbookParticipantFailure());
            }
        );
    }

    @Action(UnbookManager)
    deleteManagerEntity(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: UnbookManager): void {
        this._courseOfferingParticipantsService.unbookManager(action.payload).subscribe(
            () => {
                ctx.dispatch(new UnbookManagerSuccess());
            },
            (error) => {
                ctx.dispatch(new UnbookManagerFailure());
            }
        );
    }

    @Action(AddUsersToModulesAsParticipantsSuccess)
    addUsersToModulesAsParticipantsSuccess(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        const moduleUsers = this._store.selectSnapshot(ModuleUserState.getEntities);
        const currentModule = this._store.selectSnapshot(ModuleState.getEntity);

        if (currentModule) {
            this._setSubentities(ctx, { payload: moduleUsers.filter((moduleUser) => moduleUser.isParticipant && moduleUser.moduleId === currentModule.id) }, AddModuleUsers);
        }
    }

    @Action(RemoveUsersFromModulesAsParticipantsSuccess)
    removeUsersFromModulesAsParticipants(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        const moduleUsers = this._store.selectSnapshot(ModuleUserState.getEntities);
        const currentModule = this._store.selectSnapshot(ModuleState.getEntity);

        if (currentModule) {
            this._setSubentities(ctx, { payload: moduleUsers.filter((moduleUser) => moduleUser.isParticipant && moduleUser.moduleId === currentModule.id) }, AddModuleUsers);
        }
    }

    @Action(SearchCourseOfferingParticipants)
    search(ctx: StateContext<CourseOfferingParticipantsStateModel>, { payload }: SearchCourseOfferingParticipants): void {
        if (payload) {
            const { searchTerm, source } = payload;
            switch (source) {
                case 'manager':
                    UtilsState.searchManager(ctx, searchTerm, CourseOfferingParticipantsStateModel.identifier);
                    break;
                default:
                    UtilsState.search(ctx, searchTerm, CourseOfferingParticipantsStateModel.identifier);
                    break;
            }
        }
    }

    @Action(SortCourseOfferingParticipants)
    sort(stateContext: StateContext<CourseOfferingParticipantsStateModel>, { payload }: SortCourseOfferingParticipants): void {
        UtilsState.sort(stateContext, payload, CourseOfferingParticipantsStateModel.identifier);
    }

    @Action(ToggleSelectedParticipants)
    toggleSelect(ctx: StateContext<CourseOfferingParticipantsStateModel>, action: ToggleSelectedParticipants): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredParticipants)
    selectAll(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedFilteredParticipants)
    deselectAll(ctx: StateContext<CourseOfferingParticipantsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(NotifyAllProgramExecutionCourseOfferingParticipants)
    notifyAllProgramExecutionCourseOfferingParticipants(
        { dispatch }: StateContext<CourseOfferingParticipantsStateModel>,
        { payload }: NotifyAllProgramExecutionCourseOfferingParticipants
    ): void {
        this._courseOfferingParticipantsService
            .notifyAllProgramExecutionCourseOffering(payload.programExecutionId, payload.courseOfferingId, payload.notifyAll, payload.notifyFilters)
            .subscribe(
                () => {
                    dispatch(new NoNotifyAllProgramExecutionCourseOfferingParticipantsSuccess());
                },
                (response: HttpErrorResponse) => {
                    dispatch(new NotifyAllProgramExecutionCourseOfferingParticipantsFailure({ response }));
                }
            );
    }

    @Action(NotifySelectedProgramExecutionCourseOfferingParticipants)
    notifySelectedProgramExecutionCourseOfferingParticipants(
        { dispatch }: StateContext<CourseOfferingParticipantsStateModel>,
        { payload }: NotifySelectedProgramExecutionCourseOfferingParticipants
    ): void {
        this._courseOfferingParticipantsService
            .notifySelectedProgramExecutionCourseOffering(payload.programExecutionId, payload.courseOfferingId, payload.notifySelected, payload.notifyFilters)
            .subscribe(
                () => {
                    dispatch(new NotifySelectedProgramExecutionCourseOfferingParticipantsSuccess());
                },
                (response: HttpErrorResponse) => {
                    dispatch(new NotifySelectedProgramExecutionCourseOfferingParticipantsFailure({ response }));
                }
            );
    }

    @Action(NotifyAllCourseOfferingParticipants)
    notifyAllCourseOfferingParticipants({ dispatch }: StateContext<CourseOfferingParticipantsStateModel>, { payload }: NotifyAllCourseOfferingParticipants): void {
        this._courseOfferingParticipantsService.notifyAllCourseOffering(payload.courseOfferingId, payload.notifyAll, payload.notifyFilters).subscribe(
            () => {
                dispatch(new NoNotifyAllCourseOfferingParticipantsSuccess());
            },
            (response: HttpErrorResponse) => {
                dispatch(new NotifyAllCourseOfferingParticipantsFailure({ response }));
            }
        );
    }

    @Action(NotifySelectedCourseOfferingParticipants)
    notifySelectedCourseOfferingParticipants({ dispatch }: StateContext<CourseOfferingParticipantsStateModel>, { payload }: NotifySelectedCourseOfferingParticipants): void {
        this._courseOfferingParticipantsService.notifySelectedCourseOffering(payload.courseOfferingId, payload.notifySelected, payload.notifyFilters).subscribe(
            () => {
                dispatch(new NotifySelectedCourseOfferingParticipantsSuccess());
            },
            (response: HttpErrorResponse) => {
                dispatch(new NotifySelectedCourseOfferingParticipantsFailure({ response }));
            }
        );
    }

    @Action(FetchProgramParticipantBookings)
    fetchParticipantBookings({ dispatch }: StateContext<CourseOfferingParticipantsStateModel>, { payload }: FetchProgramParticipantBookings): void {
        this._courseOfferingParticipantsService.getParticipantBookings(payload.programExecutionId, payload.userIds, payload.context).subscribe(
            (entities) => {
                dispatch(new FetchProgramParticipantBookingsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchProgramParticipantBookingsFailure());
            }
        );
    }

    @Action(FetchProgramManagerBookings)
    fetchManagerBookings({ dispatch }: StateContext<CourseOfferingParticipantsStateModel>, { payload }: FetchProgramManagerBookings): void {
        this._courseOfferingParticipantsService.getManagerBookings(payload.programExecutionId, payload.userIds).subscribe(
            (entities) => {
                dispatch(new FetchProgramManagerBookingsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchProgramManagerBookingsFailure());
            }
        );
    }
}
